import React, { useState, useRef } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  Add,
  Inventory2,
  Refresh,
  AccessTime,
  Undo,
  Category,
  Schedule,
  People,
  AutoAwesome,
  ImageSearch,
  FactCheck,
  Settings as SettingsIcon,
  LocalShipping,
  CheckCircle,
  ArrowForward,
  VpnKey,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { FeatureKey } from '../rbac/features';
import { WarehouseAPI } from '../services/api';
import { tokens } from '../theme/tokens';
import { TOUR } from '../components/onboarding/tourTargets';
import { useToast } from '../components/common/useToast';

// Shared Actionables-grade motion easing — keeps Dashboard entrances/hovers on the
// same curve as the rest of the Salty UI.
const EASE = [0.16, 1, 0.3, 1] as const;

// Salty brand orange — reused for the primary daily action and the hero sync button.
const SALTY_ORANGE = tokens.orange;
const SALTY_ORANGE_HOVER = tokens.orangeDark;
// Neutral warm-grey icon chip (Admin pills only).
const NEUTRAL_ICON = tokens.muted;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reduceMotion = useReducedMotion();
  const toast = useToast();
  // Global status fetched from backend so other users/browsers see running state
  const [globalRefreshRunning, setGlobalRefreshRunning] = useState<boolean>(false);
  const [createBlockedMessage, setCreateBlockedMessage] = useState<string | null>(null);
  // Inventory update notice state — drives the non-blocking, dismissible dialog.
  // phase: 'running' while the sync is in-flight, 'completed' once ShelfWiseInventory
  // reports a strictly newer updatedAt, 'timeout' if the 10-minute safety window elapses.
  const [inventoryUpdateNotice, setInventoryUpdateNotice] = useState<{
    open: boolean;
    phase: 'running' | 'completed' | 'timeout';
    message?: string | null;
    freshAt?: string | null;
  }>({ open: false, phase: 'running', message: null, freshAt: null });
  // Sync-tracking refs (not state — must not retrigger effects and must survive re-render)
  const syncStartedAtRef = useRef<number | null>(null);
  const beforeUpdatedAtRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();
  // Sync Now + the "Inventory synced" freshness pill are gated on the sync-inventory EDIT
  // capability (matches the backend requirePermission on the sync routes).
  const canSeeInventorySync = usePermission('sync-inventory').can('edit');
  // Per-feature view capabilities — consumed by the canViewFeature map below to decide
  // which cards render (a hidden feature's card is dropped so no user sees a tile linking
  // to a page they cannot reach).
  const canViewImageGen = usePermission('image-generation').can('view');
  const canViewInventory = usePermission('view-inventory').can('view');
  const canViewBundleSkus = usePermission('bundle-skus').can('view');
  const canViewCyclicCount = usePermission('cyclic-count').can('view');
  const canViewCarding = usePermission('carding').can('view');
  const canViewActivities = usePermission('activities').can('view');

  // Removed API calls that were causing 400 errors:
  // - WarehouseAPI.getDashboardStats()
  // - WarehouseAPI.getRecentActivity()

  // Stop the completion poller and clear the safety timeout. Safe to call repeatedly.
  const stopSyncPolling = React.useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
  }, []);

  // Start polling ShelfWiseInventory for a REAL completion signal: a strictly newer
  // `updatedAt` than the baseline captured at trigger time. A null baseline cannot be
  // proven to advance, so completion then relies solely on the 10-minute safety timeout.
  // Transient network errors are swallowed — only the timeout ends the sync.
  const startSyncPolling = React.useCallback(() => {
    // Never stack intervals/timeouts across two rapid syncs.
    stopSyncPolling();

    pollIntervalRef.current = setInterval(() => {
      WarehouseAPI.getInventoryFreshness()
        .then((latest) => {
          const baseline = beforeUpdatedAtRef.current;
          const advanced =
            !!latest &&
            !!baseline &&
            new Date(latest).getTime() > new Date(baseline).getTime();
          if (!advanced) return; // keep waiting (null baseline => wait for timeout)

          // Completion — real signal observed.
          stopSyncPolling();
          setInventoryUpdateNotice({ open: true, phase: 'completed', message: null, freshAt: latest });
          WarehouseAPI.finishUnicommerceStatus({ finishedAt: new Date().toISOString() }).catch(() => {/* non-blocking: 400 when no running job */ });
          setGlobalRefreshRunning(false);
          window.dispatchEvent(new Event('uc-sync-changed'));
          // Auto-dismiss the success notice shortly after, only if still showing completed.
          setTimeout(() => {
            setInventoryUpdateNotice((prev) => (prev.phase === 'completed' ? { ...prev, open: false } : prev));
          }, 5000);
        })
        .catch((err) => {
          // A single failed poll must NOT end the sync — just log and keep polling.
          console.warn('Inventory sync poll failed (will retry):', err);
        });
    }, 10000);

    // Safety timeout: 10 minutes from sync start. If updatedAt never advances, stop.
    const elapsed = syncStartedAtRef.current ? Date.now() - syncStartedAtRef.current : 0;
    const remaining = Math.max(0, 10 * 60 * 1000 - elapsed);
    safetyTimeoutRef.current = setTimeout(() => {
      stopSyncPolling();
      setInventoryUpdateNotice({
        open: true,
        phase: 'timeout',
        message: 'Sync is taking longer than expected. Inventory may still be updating — check n8n or try again.',
        freshAt: null,
      });
      WarehouseAPI.finishUnicommerceStatus({ finishedAt: new Date().toISOString() }).catch(() => {/* non-blocking */ });
      setGlobalRefreshRunning(false);
      window.dispatchEvent(new Event('uc-sync-changed'));
    }, remaining);
  }, [stopSyncPolling]);

  // Refresh stock mutation
  const refreshStockMutation = useMutation({
    mutationFn: () => WarehouseAPI.refreshStockFromUnicommerce(),
    onMutate: () => {
      // Record start time and defensively clear any in-flight poller/timeout.
      syncStartedAtRef.current = Date.now();
      stopSyncPolling();
      // Open the non-blocking, dismissible running notice.
      setInventoryUpdateNotice({
        open: true,
        phase: 'running',
        message: "Inventory sync running — you can keep working. We'll mark inventory fresh when it lands.",
        freshAt: null,
      });
      setGlobalRefreshRunning(true);
      // Notify backend that a refresh job has started (fire-and-forget) so other
      // sessions see isRunning=true immediately.
      WarehouseAPI.startUnicommerceStatus({ serverMessage: 'Triggered from UI' }).catch(() => {/* non-blocking */ });
      // Let the Header chip appear promptly.
      window.dispatchEvent(new Event('uc-sync-changed'));
      // Capture the baseline updatedAt; a null baseline is acceptable (timeout governs).
      WarehouseAPI.getInventoryFreshness()
        .then((latest) => { beforeUpdatedAtRef.current = latest; })
        .catch(() => { beforeUpdatedAtRef.current = null; });
      // Start the real completion poller (created in exactly one place).
      startSyncPolling();
    },
    onSuccess: () => {
      // The webhooks return a plain string with NO real completion signal, so do NOT
      // mark the sync complete here — the poller owns completion. Just refresh queries.
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (error: any) => {
      toast.error(`Failed to refresh stock: ${error.message}`, { duration: 6000 });
      // Stop the sync machinery and clear the running state so the chip/notice clear.
      stopSyncPolling();
      setGlobalRefreshRunning(false);
      setInventoryUpdateNotice((prev) => ({ ...prev, open: false }));
      WarehouseAPI.finishUnicommerceStatus({ finishedAt: new Date().toISOString() }).catch(() => {/* non-blocking */ });
      window.dispatchEvent(new Event('uc-sync-changed'));
    },
  });

  // Clear any running poller/timeout on unmount.
  React.useEffect(() => () => stopSyncPolling(), [stopSyncPolling]);

  // Poll global Unicommerce running status so UI can disable actions across sessions
  React.useEffect(() => {
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const res = await WarehouseAPI.getUnicommerceStatus();
        // WarehouseAPI.getUnicommerceStatus returns res.data from the proxy
        const data = res;
        if (!cancelled) setGlobalRefreshRunning(!!(data && data.isRunning));
      } catch (err) {
        // ignore network errors — don't flip the running flag on failure
        if (!cancelled) console.warn('Failed to fetch Unicommerce status', err);
      }
    };

    // Initial check
    checkStatus();
    // Poll every 10 seconds while component is mounted
    const interval = setInterval(checkStatus, 10000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const handleCreateAction = async () => {
    try {
      // Primary check: ensure shelfwise-inventory has recent data
      // Fetch a single item from shelfwise-inventory and check its updatedAt
      try {
        const shelfResp = await WarehouseAPI.getShelfWiseInventory('', 0, 1);
        const item = shelfResp?.data && shelfResp.data.length > 0 ? shelfResp.data[0] : null;
        if (item && item.updatedAt) {
          const updatedTs = new Date(item.updatedAt).getTime();
          const ageMs = Date.now() - updatedTs;
          const fiveMinutes = 5 * 60 * 1000;
          if (ageMs <= fiveMinutes) {
            navigate('/create-bundle');
            return;
          }
          setCreateBlockedMessage('Last sync was older than 5 minutes. Please sync inventory before creating a bundle.');
          return;
        }
        // If no shelfwise data present, fall back to Unicommerce status check below
      } catch (shelfErr) {
        // If shelfwise inventory check fails, continue to fallback to Unicommerce status verification
        console.warn('Shelfwise inventory check failed, falling back to UC status check', shelfErr);
      }

      // Fallback: Check global Unicommerce status from backend
      const status = await WarehouseAPI.getUnicommerceStatus();
      // If a sync is currently running, inform the user
      if (status?.isRunning) {
        const msg = status.serverMessage || 'Inventory sync is currently running. Please wait until it completes.';
        setCreateBlockedMessage(msg);
        return;
      }

      // If finishedAt is present, ensure it's within the last 10 minutes
      if (status?.finishedAt) {
        const finishedTs = new Date(status.finishedAt).getTime();
        const ageMs = Date.now() - finishedTs;
        const tenMinutes = 10 * 60 * 1000;
        if (ageMs <= tenMinutes) {
          navigate('/create-bundle');
          return;
        }
        // too old
        setCreateBlockedMessage('Last sync was older than 10 minutes. Please sync inventory before creating a bundle.');
        return;
      }

      // No finishedAt recorded — prompt to sync first
      setCreateBlockedMessage('No recent inventory sync found. Please sync inventory before creating a bundle.');
    } catch (err) {
      // On error, be permissive but provide guidance
      setCreateBlockedMessage('Could not verify inventory sync status. Please try again or sync inventory before creating a bundle.');
    }
  };

  const isInventorySyncing = globalRefreshRunning;

  // ── Dashboard data ─────────────────────────────────────────────────────────
  // The hero's freshness chip is inventory-sync-gated; the "to do" count runs for every
  // authenticated user (matching the always-shown "To do" pill). Neither blocks the page
  // render. React Query global defaults already apply retry:1 + refetchOnWindowFocus:false.

  // (b) Inventory-sync freshness — newest ShelfwiseInventory updatedAt, from this backend's
  // Mongo (reachable in dev and prod), not the prod-only /apis inventory proxy.
  const freshnessQuery = useQuery({
    queryKey: ['dash-sync-freshness'],
    queryFn: () => WarehouseAPI.getInventoryFreshness(),
    enabled: canSeeInventorySync,
  });

  // (c) My actionables — "to do" count for the hero.
  const actionablesQuery = useQuery({
    queryKey: ['dash-actionables'],
    queryFn: () => WarehouseAPI.getMyActionableCount(),
    enabled: !!user,
  });

  const freshnessUpdatedAt = freshnessQuery.data ?? null;

  // ── Hero greeting / date (IST) ─────────────────────────────────────────────
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || 'there';
  const istDateLabel = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date()).toUpperCase();
  const istHour = parseInt(
    new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: 'numeric',
      hour12: false,
    }).format(new Date()),
    10
  );
  const greeting =
    istHour < 12 ? 'Good morning' : istHour < 17 ? 'Good afternoon' : 'Good evening';

  const formatIstDateTime = (iso: string) =>
    new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));

  // ── Role-gated action definitions (same actions + gating as before) ────────
  type ActionDef = {
    title: string;
    description: string;
    action: () => void;
    icon: React.ReactNode;
    iconBg: string;
    iconSolid?: boolean; // true = solid fill + white icon (primary actions); else soft tint
    disabled?: boolean;
    dataTour?: string; // optional data-tour anchor for the guided tour
    feature?: FeatureKey; // RBAC feature gating this card's visibility (view+)
    visible?: boolean; // explicit visibility override (used when the gate isn't a plain feature view)
  };

  // Per-feature view capability — drives which cards render (a hidden feature's card is
  // dropped so no user sees a tile linking to a page they cannot reach).
  const canViewFeature: Record<string, boolean> = {
    'add-bundle': usePermission('add-bundle').can('view'),
    'reduce-bundle': usePermission('reduce-bundle').can('view'),
    carding: canViewCarding,
    'cyclic-count': canViewCyclicCount,
    'bundle-skus': canViewBundleSkus,
    'bundle-combination': usePermission('bundle-combination').can('view'),
    'view-inventory': canViewInventory,
    'saved-drafts': usePermission('saved-drafts').can('view'),
    activities: canViewActivities,
    'image-generation': canViewImageGen,
    'manage-users': usePermission('manage-users').can('view'),
    settings: usePermission('settings').can('view'),
  };
  // Access Control is admin-only: gated on access-control EDIT (matches the Sidebar entry and
  // the /access-control route guard), NOT the plain manage-users view used by other cards.
  const canViewAccessControl = usePermission('access-control').can('edit');
  const cardVisible = (a: ActionDef) =>
    a.visible !== undefined ? a.visible : (!a.feature || canViewFeature[a.feature]);

  const dailyWorkflow: ActionDef[] = [
    {
      title: 'Add bundle Inventory',
      description: isInventorySyncing
        ? 'Inventory syncing in progress, try in few minutes'
        : 'Start the bundle creation workflow',
      action: () => handleCreateAction(),
      icon: <Add />,
      iconBg: SALTY_ORANGE,
      iconSolid: true,
      disabled: isInventorySyncing,
      dataTour: TOUR.CARD_ADD_BUNDLE,
      feature: 'add-bundle',
    },
    {
      title: 'Reduce bundle Inventory',
      description: isInventorySyncing
        ? 'Inventory syncing in progress, try in few minutes'
        : 'Break bundles into child SKUs',
      action: () => navigate('/debundle'),
      icon: <Undo />,
      iconBg: SALTY_ORANGE,
      disabled: isInventorySyncing,
      dataTour: TOUR.CARD_REDUCE_BUNDLE,
      feature: 'reduce-bundle',
    },
    {
      title: 'Carding',
      description: 'Move SKUs from NONCARDED shelves to carded, with packaging deduction from Plytix',
      action: () => navigate('/carding'),
      icon: <LocalShipping />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_CARDING,
      feature: 'carding',
    },
    {
      title: 'Cyclic Count',
      description: 'Physical inventory count with variance detection and Unicommerce adjustment',
      action: () => navigate('/cyclic-count'),
      icon: <FactCheck />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_CYCLIC_COUNT,
      feature: 'cyclic-count',
    },
  ];

  const catalogInsights: ActionDef[] = [
    {
      title: 'Bundle SKUs',
      description: 'Manage bundle SKUs and child mappings',
      action: () => navigate('/bundle-categories'),
      icon: <Category />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_BUNDLE_SKUS,
      feature: 'bundle-skus',
    },
    {
      title: 'Bundle Combination',
      description: 'AI-powered bundle suggestions based on DRR, inventory & sales data',
      action: () => navigate('/bundle-combination'),
      icon: <AutoAwesome />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_BUNDLE_COMBINATION,
      feature: 'bundle-combination',
    },
    {
      title: 'View Inventory',
      description: 'Check current stock levels',
      action: () => navigate('/inventory'),
      icon: <Inventory2 />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_VIEW_INVENTORY,
      feature: 'view-inventory',
    },
    {
      title: 'Saved Drafts',
      description: 'Resume or manage partially completed bundles',
      action: () => navigate('/drafts'),
      icon: <Schedule />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_SAVED_DRAFTS,
      feature: 'saved-drafts',
    },
    {
      title: 'Recent Activities',
      description: 'View recent child-deductions and bundle creations',
      action: () => navigate('/activities'),
      icon: <AccessTime />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_RECENT_ACTIVITIES,
      feature: 'activities',
    },
    // Image Gen lives visually in Catalog & Insights. Its visibility is now driven by
    // the image-generation view capability (cardVisible filter below) — the non-photography
    // dashboard shows it to any role that can view image generation. Photography-only users
    // get it via the dedicated photography-only view earlier in this component.
    {
      title: 'Image Generation',
      description: 'Generate AI product photography composites',
      action: () => navigate('/image-generation'),
      icon: <ImageSearch />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_IMAGE_GEN,
      feature: 'image-generation',
    },
  ];

  // ADMIN pills. (Image Generation lives in the CATALOG section,
  // but it keeps its admin-only visibility — see the catalogInsights build below.)
  const adminTools: ActionDef[] = [
    {
      title: 'Manage user',
      description: 'Create, edit, or delete application users',
      action: () => navigate('/admin/users'),
      icon: <People />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_MANAGE_USERS,
      feature: 'manage-users',
    },
    {
      title: 'Access Control',
      description: 'See users\u2019 last sign-in and approximate location',
      action: () => navigate('/access-control'),
      icon: <VpnKey />,
      iconBg: SALTY_ORANGE,
      visible: canViewAccessControl, // admin-only (access-control edit), matches the route guard
    },
    {
      title: 'System Settings',
      description: 'Manage WMS configurations and excluded shelves',
      action: () => navigate('/settings'),
      icon: <SettingsIcon />,
      iconBg: SALTY_ORANGE,
      dataTour: TOUR.CARD_SYSTEM_SETTINGS,
      feature: 'settings',
    },
  ];

  // Admin & Tools group shows when the caller can view at least one of its features.
  const visibleAdminTools = adminTools.filter(cardVisible);
  const showAdminTools = visibleAdminTools.length > 0;

  // ── Reusable renderers ─────────────────────────────────────────────────────

  // A single action card. The whole card is the click target (keyboard accessible)
  // unless disabled. Consistent white surface + small colored icon chip; '→'
  // affordance appears on hover. Framer-motion entrance/hover-lift are gated by
  // useReducedMotion and never gate the underlying action (the onClick fires
  // immediately regardless of the animation state).
  const renderActionCard = (action: ActionDef, key: React.Key, index = 0, dataTour?: string) => {
    const clickable = !action.disabled;
    const activate = () => { if (clickable) action.action(); };
    return (
      <motion.div
        key={key}
        data-tour={dataTour}
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={reduceMotion || !clickable ? undefined : { y: -3 }}
        transition={{ duration: 0.3, delay: Math.min(index, 10) * 0.05, ease: EASE }}
        style={{ height: '100%' }}
      >
        <Card
          className="hover-lift"
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={activate}
          onKeyDown={(e) => {
            if (!clickable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activate();
            }
          }}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            cursor: clickable ? 'pointer' : 'not-allowed',
            opacity: action.disabled ? 0.6 : 1,
            background: tokens.surface,
            border: '1px solid',
            borderColor: tokens.border,
            boxShadow: tokens.softShadow,
            '&:hover': {
              borderColor: clickable ? action.iconBg : tokens.border,
              boxShadow: clickable ? '0 2px 4px rgba(26,20,15,0.05), 0 16px 36px rgba(26,20,15,0.10)' : tokens.softShadow,
              '& .action-icon': { transform: clickable ? 'scale(1.08)' : 'none' },
              '& .action-arrow': { opacity: clickable ? 1 : 0, transform: clickable ? 'translateX(0)' : 'translateX(-4px)' },
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box
                className="action-icon"
                sx={{
                  mr: 2,
                  p: 1.25,
                  borderRadius: 2,
                  // OPTION 1 (cohesive/calm): primary actions (Add/Reduce) use a SOLID fill and
                  // white icon so they stand out; every other card uses a SOFT TINT of its accent
                  // (light bg + colored icon) so nothing is a flat "blank" grey chip but the two
                  // key actions still pop. iconSolid flags the solid ones.
                  ...(action.iconSolid
                    ? { bgcolor: action.iconBg, color: '#ffffff' }
                    : { bgcolor: `${action.iconBg}1F`, color: action.iconBg }),
                  display: 'flex',
                  transition: 'transform 0.25s ease-in-out',
                }}
              >
                {action.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: tokens.ink, mb: 0.5 }}>
                  {action.title}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5, color: tokens.muted }}>
                  {action.description}
                </Typography>
              </Box>
              <ArrowForward
                className="action-arrow"
                sx={{
                  ml: 1,
                  fontSize: 20,
                  color: action.iconBg,
                  opacity: 0,
                  transform: 'translateX(-4px)',
                  transition: 'all 0.25s ease-in-out',
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  // A compact, quiet pill for the Admin & Tools group.
  const renderAdminPill = (action: ActionDef, key: React.Key, index = 0, dataTour?: string) => (
    <motion.div
      key={key}
      data-tour={dataTour}
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={reduceMotion ? undefined : { y: -2 }}
      transition={{ duration: 0.3, delay: Math.min(index, 10) * 0.05, ease: EASE }}
    >
      <Card
        className="hover-lift"
        role="button"
        tabIndex={0}
        onClick={() => action.action()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            action.action();
          }
        }}
        sx={{
          cursor: 'pointer',
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          boxShadow: tokens.softShadow,
          '&:hover': { borderColor: NEUTRAL_ICON, background: '#FAFAF7' },
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ display: 'flex', color: NEUTRAL_ICON }}>{action.icon}</Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.ink }}>
            {action.title}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderSectionHeading = (label: string) => (
    <Typography
      variant="overline"
      sx={{ display: 'block', mb: 0.75, color: tokens.muted, fontWeight: 700, letterSpacing: '0.12em' }}
    >
      {label}
    </Typography>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── DARK STATUS HERO ────────────────────────────────────────────── */}
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <Card
          sx={{
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            mb: 2.5,
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: 'none',
            background: tokens.heroGradient,
            color: '#ffffff',
            boxShadow: '0 10px 40px rgba(26,20,15,0.28)',
          }}
        >
          {/* Warm orange radial glow overlay — purely decorative. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: tokens.orangeGlow,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            {/* Left: date eyebrow + greeting + status sentence */}
            <Box>
              <Typography
                variant="overline"
                sx={{ color: tokens.orangeLight, fontWeight: 700, letterSpacing: '0.12em' }}
              >
                {istDateLabel}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#ffffff', mt: 0.5 }}>
                {greeting}, {firstName}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.72)', mt: 1 }}>
                {isInventorySyncing
                  ? 'Inventory sync is running — you can keep working while it lands.'
                  : "Here's where your warehouse stands right now."}
              </Typography>
            </Box>

            {/* Right: two equal-weight status pills (synced + to-do) and the Sync Now button,
                all on one baseline-aligned row. The status pills share identical styling and
                sit in their own group; the button is separated by a divider so the row reads as
                "status · action" rather than three mismatched elements floating at odd heights. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              {/* Status pills group — the "Synced" pill is inventory-sync-gated; "To do" is not. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {canSeeInventorySync && (
                  freshnessQuery.isLoading ? (
                    <Skeleton variant="rounded" width={210} height={30} sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '999px' }} />
                  ) : (
                    <Chip
                      icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      label={`Synced ${freshnessUpdatedAt ? formatIstDateTime(freshnessUpdatedAt) : '—'}`}
                      sx={{
                        height: 30,
                        bgcolor: 'rgba(255,255,255,0.10)',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        border: '1px solid rgba(255,255,255,0.18)',
                        '& .MuiChip-icon': { color: '#4ADE80' },
                      }}
                    />
                  )
                )}
                <Chip
                  label={
                    <span>
                      To do&nbsp;
                      <Box component="span" sx={{ fontWeight: 800 }}>
                        {actionablesQuery.isLoading ? '…' : actionablesQuery.isError ? '—' : actionablesQuery.data?.open ?? 0}
                      </Box>
                    </span>
                  }
                  sx={{
                    height: 30,
                    bgcolor: 'rgba(255,255,255,0.10)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    border: '1px solid rgba(255,255,255,0.18)',
                  }}
                />
              </Box>

              {/* Inventory-sync action — divider + Sync Now button, restricted to allowed roles. */}
              {canSeeInventorySync && (
                <>
                  <Box sx={{ width: '1px', height: 26, bgcolor: 'rgba(255,255,255,0.18)', display: { xs: 'none', md: 'block' } }} />

                  <Button
                    variant="contained"
                    startIcon={
                      refreshStockMutation.isPending
                        ? <CircularProgress size={16} color="inherit" />
                        : <Refresh sx={{ fontSize: 18 }} />
                    }
                    onClick={() => refreshStockMutation.mutate()}
                    disabled={refreshStockMutation.isPending}
                    sx={{
                      height: 40,
                      px: 2.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: '999px',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      bgcolor: SALTY_ORANGE,
                      color: '#ffffff',
                      '&:hover': { bgcolor: SALTY_ORANGE_HOVER, boxShadow: '0px 6px 18px rgba(220, 88, 41, 0.45)' },
                      '&.Mui-disabled': { bgcolor: 'rgba(220, 88, 41, 0.55)', color: 'rgba(255,255,255,0.85)' },
                    }}
                  >
                    {refreshStockMutation.isPending ? 'Syncing…' : 'Sync Now'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Card>
      </motion.div>

      {/* ── DAILY WORKFLOW ──────────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        {renderSectionHeading('DAILY WORKFLOW')}
        {/* Balanced fixed columns: 4 daily cards = clean 2×2 on desktop, 1-up on mobile.
            Cards stretch equally to fill the width (no right-side gap, no orphan rows). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 1.5,
          }}
        >
          {dailyWorkflow.filter(cardVisible).map((a, i) => renderActionCard(a, `daily-${i}`, i, a.dataTour))}
        </Box>
      </Box>

      {/* ── CATALOG ─────────────────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        {renderSectionHeading('CATALOG')}
        {/* 6 catalog cards = clean 3×2 on desktop, 2-up tablet, 1-up mobile. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          {catalogInsights.filter(cardVisible).map((a, i) => renderActionCard(a, `catalog-${i}`, i, a.dataTour))}
        </Box>
      </Box>

      {/* ── ADMIN ───────────────────────────────────────────────────────── */}
      {showAdminTools && (
        <Box sx={{ mb: 2 }}>
          {renderSectionHeading('ADMIN')}
          {/* Admin pills: 2-up on desktop, 1-up on mobile. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
            }}
          >
            {visibleAdminTools.map((a, i) => renderAdminPill(a, `admin-${i}`, i, a.dataTour))}
          </Box>
        </Box>
      )}

      <Dialog
        open={!!createBlockedMessage}
        onClose={() => setCreateBlockedMessage(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            border: '2px solid #f59e0b',
          }
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box sx={{ mb: 3 }}>
            <AccessTime
              sx={{
                fontSize: 48,
                color: '#f59e0b',
                mb: 2,
                display: 'block',
                mx: 'auto'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#ffffff',
                mb: 2,
                fontSize: '1.5rem',
                lineHeight: 1.3,
              }}
            >
              Inventory Sync Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#f59e0b',
                fontSize: '1.1rem',
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {createBlockedMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setCreateBlockedMessage(null)}
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#f59e0b',
              color: '#1A1410', // dark ink on amber (~9.9:1); white would fail (~2:1). Beats central containedPrimary white.
              '&:hover': { bgcolor: '#d97706' },
              px: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inventory Update Notice Dialog — non-blocking & dismissible at all times */}
      <Dialog
        open={inventoryUpdateNotice.open}
        onClose={() => setInventoryUpdateNotice((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 2,
            py: 1,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          {inventoryUpdateNotice.phase === 'completed' ? (
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'success.main' }}>
              Inventory updated ✓
            </Typography>
          ) : inventoryUpdateNotice.phase === 'timeout' ? (
            <Typography variant="h5" fontWeight="bold" sx={{ color: 'warning.main' }}>
              Sync taking longer than expected
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress color="primary" size={48} />
              <Typography variant="h5" fontWeight="bold">
                Inventory Sync in Progress
              </Typography>
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          {inventoryUpdateNotice.phase === 'completed' && inventoryUpdateNotice.freshAt ? (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Fresh as of{' '}
              {new Intl.DateTimeFormat('en-IN', {
                timeZone: 'Asia/Kolkata',
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(inventoryUpdateNotice.freshAt))}
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {inventoryUpdateNotice.message}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pt: 0, pb: 2 }}>
          <Button
            onClick={() => setInventoryUpdateNotice((prev) => ({ ...prev, open: false }))}
            variant="contained"
            size="large"
            sx={{ minWidth: 120 }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
